import { useEffect, useState, useRef, useMemo } from 'react';
import SiteLink from 'components/shared/sitelink/sitelink';
import classNames from 'classnames';
import useScrollDown from 'hooks/utils/useScrolldown';
import useIsMobile from 'hooks/utils/useIsMobile';
import ArrowUp from 'components/shared/arrow-up';
import ArrowDown from 'components/shared/arrow-down';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import styles from './professionalsHeader.module.scss';

const ProfessionalsHeader = ({ data, locale }) => {
  const { navbar, button, resources } = data;
  const hasAnnouncementBar =
    resources['announcement-bar']['announcementBar-AllowedLocales']?.onOrOff;
  const [selectedLink, setSelectedLink] = useState(null);
  const [openedMobileMenu, setOpenedMobileMenu] = useState(false);
  const { positionTop } = useScrollDown();
  const isMobile = useIsMobile();
  const headerRef = useRef();
  const {
    navbar: { submenus },
  } = data;
  const mobileSubmenus = useMemo(
    () => navbar.submenus.filter(submenu => submenu !== selectedLink).slice(0, 5),
    [selectedLink, navbar.submenus]
  );
  const router = useRouter();

  // Find the link that is the same as the current [slug]
  useEffect(() => {
    const splittedText = window.location.pathname.split(`${locale}`);
    const currentSlug = splittedText.find(part => part !== '/').split('/')[1];
    setSelectedLink(submenus.find(submenu => submenu.link.slug === currentSlug));
  }, [navbar.submenus]);

  return (
    <div
      id="sectionProfessionalsHeader"
      className={classNames(styles.professionalsHeader, {
        [styles.scrolled]: !positionTop || (isMobile && openedMobileMenu),
        [styles.noAnnouncementBar]: !hasAnnouncementBar,
      })}
      ref={headerRef}
    >
      <nav>
        {!isMobile && (
          <ul className={styles.professionalsLinks}>
            {submenus.slice(0, 5).map(submenu => (
              <li
                className={classNames(styles.navItem, {
                  [styles.selected]: selectedLink === submenu,
                  [styles.scrolledNavItem]: !positionTop,
                })}
                key={submenu.link.title}
              >
                <SiteLink
                  locale={locale}
                  reloaded={submenu.link.pageReload}
                  external={submenu.link.externalLink}
                  href={addLocaleToHref(submenu.link.slug, locale, router)}
                >
                  <h2>{submenu.link.title}</h2>
                </SiteLink>
              </li>
            ))}
          </ul>
        )}
        {isMobile && (
          <>
            <button
              type="button"
              className={classNames(styles.mobileSelectedNavItem, {
                [styles.mobileScrolledNavItem]: !positionTop || openedMobileMenu,
              })}
              onClick={() => setOpenedMobileMenu(!openedMobileMenu)}
            >
              {selectedLink?.link?.title}
              <span
                className={classNames(styles.mobileMenuArrow, {
                  [styles.scrolledMobileMenuArrow]: !positionTop || openedMobileMenu,
                })}
              >
                {openedMobileMenu ? <ArrowUp /> : <ArrowDown />}
              </span>
            </button>
            <div className={classNames(styles.mobileDrawer, { [styles.opened]: openedMobileMenu })}>
              <ul className={styles.mobileMenuLinkList}>
                {mobileSubmenus.map(mobileSubmenu => (
                  <li key={mobileSubmenu} className={styles.mobileMenuLink}>
                    <SiteLink
                      locale={locale}
                      onClick={() => setOpenedMobileMenu(false)}
                      reloaded={mobileSubmenu.link.pageReload}
                      external={mobileSubmenu.link.externalLink}
                      href={addLocaleToHref(mobileSubmenu.link.slug, locale, router)}
                    >
                      <h2>{mobileSubmenu.link.title}</h2>
                    </SiteLink>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </nav>
      <div
        className={classNames(styles.navbarButton, {
          [styles.scrolledNavbarButton]: !positionTop || (isMobile && openedMobileMenu),
        })}
      >
        <SiteLink locale={locale} href={addLocaleToHref(button?.link?.slug, locale, router)}>
          {button?.title}
        </SiteLink>
      </div>
    </div>
  );
};

export default ProfessionalsHeader;
